import { useEffect, useMemo, useState } from 'react';
import Toast, { useToast } from '../../components/Toast';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { StatTile } from '../../components/StatTile';
import ProfileTabBar from '../../components/ProfileTabBar';
import DataTable from '../../components/DataTable';
import {
  REVIEW_TYPES, REVIEW_TYPE_LABEL, REVIEW_COMPETENCIES,
  getPerformanceReviews, addPerformanceReview, updatePerformanceReview, reviewDueStatus,
} from '../../lib/performance';
import { format } from 'date-fns';

const STATUS_OPTS = ['draft', 'submitted', 'acknowledged'];

const EMPTY_RATINGS = Object.fromEntries(REVIEW_COMPETENCIES.map(c => [c.key, '']));
const EMPTY_FORM = {
  staff_id: '', review_type: 'annual', review_date: new Date().toISOString().split('T')[0],
  period_start: '', period_end: '', ratings: EMPTY_RATINGS,
  strengths: '', areas_for_improvement: '', goals_next_period: '',
  related_incident_id: '', status: 'draft', staff_acknowledged: false, signed_name: '', signed_at: '',
};

const statusColor = s => ({
  draft:        { bg: 'var(--bg2)',        color: 'var(--text3)' },
  submitted:    { bg: 'var(--info-bg)',    color: 'var(--info)' },
  acknowledged: { bg: 'var(--success-bg)', color: 'var(--success)' },
}[s] || { bg: 'var(--bg2)', color: 'var(--text3)' });

const StatusPill = ({ status }) => (
  <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: status.bg, color: status.color }}>{status.label}</span>
);

const pill = (label, style) => (
  <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap', ...style }}>{label}</span>
);

export default function PerformanceMgmt() {
  const { profile } = useAuth();
  const [tab, setTab] = useState('all');
  const [reviews, setReviews] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('All');
  const [staffFilter, setStaffFilter] = useState('');
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const { toast, showToast: _showToast, hideToast } = useToast();
  const [incidentOptions, setIncidentOptions] = useState([]);

  const showToast = (msg, ms = 4000) => _showToast(msg, 'success', ms);

  const load = async () => {
    setLoading(true);
    const [{ data: reviewRows, error: reviewErr }, { data: staffRows, error: staffErr }] = await Promise.all([
      getPerformanceReviews(),
      supabase.from('staff').select('id, full_name, first_name, last_name, start_date').eq('status', 'active').order('last_name'),
    ]);
    if (reviewErr || staffErr) setError('Failed to load: ' + (reviewErr || staffErr).message);
    setReviews(reviewRows || []);
    setStaffOptions(staffRows || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Load this staff member's incident history when a PIP review references one.
  // The `cancelled` guard stops an out-of-order response (e.g. switching from
  // Staff A to Staff B before A's slower query resolves) from overwriting the
  // dropdown with the wrong staff member's incident history.
  useEffect(() => {
    if (!form.staff_id || form.review_type !== 'pip') { setIncidentOptions([]); return; }
    let cancelled = false;
    supabase.from('staff_incidents').select('id, incident_date, category').eq('staff_id', form.staff_id).order('incident_date', { ascending: false })
      .then(({ data }) => { if (!cancelled) setIncidentOptions(data || []); });
    return () => { cancelled = true; };
  }, [form.staff_id, form.review_type]);

  const reviewsByStaff = useMemo(() => {
    const map = {};
    reviews.forEach(r => { (map[r.staff_id] = map[r.staff_id] || []).push(r); });
    return map;
  }, [reviews]);

  const openAdd = (staffId) => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, staff_id: staffId || '' });
    setError('');
    setShowModal(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      staff_id: r.staff_id, review_type: r.review_type, review_date: r.review_date || '',
      period_start: r.period_start || '', period_end: r.period_end || '',
      ratings: { ...EMPTY_RATINGS, ...(r.ratings || {}) },
      strengths: r.strengths || '', areas_for_improvement: r.areas_for_improvement || '', goals_next_period: r.goals_next_period || '',
      related_incident_id: r.related_incident_id || '', status: r.status || 'draft',
      staff_acknowledged: !!r.staff_acknowledged, signed_name: r.signed_name || '', signed_at: r.signed_at ? r.signed_at.split('T')[0] : '',
    });
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.staff_id) { setError('Select a staff member'); return; }
    if (!form.review_date) { setError('Review date is required'); return; }
    setSaving(true);
    setError('');

    const ratings = Object.fromEntries(Object.entries(form.ratings).filter(([, v]) => v !== '').map(([k, v]) => [k, Number(v)]));
    const payload = {
      staff_id: form.staff_id,
      review_type: form.review_type,
      review_date: form.review_date,
      period_start: form.period_start || null,
      period_end: form.period_end || null,
      reviewer_id: editing?.reviewer_id || profile?.id || null,
      reviewer_name: editing?.reviewer_name || profile?.full_name || null,
      ratings,
      strengths: form.strengths || null,
      areas_for_improvement: form.areas_for_improvement || null,
      goals_next_period: form.goals_next_period || null,
      related_incident_id: form.review_type === 'pip' ? (form.related_incident_id || null) : null,
      status: form.status,
      staff_acknowledged: form.staff_acknowledged,
      signed_name: form.staff_acknowledged ? (form.signed_name || null) : null,
      signed_at: form.staff_acknowledged ? (form.signed_at ? new Date(form.signed_at).toISOString() : new Date().toISOString()) : null,
    };

    const { error: err } = editing
      ? await updatePerformanceReview(editing.id, payload)
      : await addPerformanceReview(payload);

    setSaving(false);
    if (err) { setError(err.message); return; }
    setShowModal(false);
    showToast(`Review ${editing ? 'updated' : 'saved'}.`);
    load();
  };

  const filtered = reviews
    .filter(r => typeFilter === 'All' || r.review_type === typeFilter)
    .filter(r => !staffFilter || r.staff_id === staffFilter);

  const thisYear = new Date().getFullYear();
  const stats = {
    overdue:   staffOptions.filter(s => reviewDueStatus(s, reviewsByStaff[s.id]).key === 'overdue').length,
    dueSoon:   staffOptions.filter(s => reviewDueStatus(s, reviewsByStaff[s.id]).key === 'due_soon').length,
    openPips:  reviews.filter(r => r.review_type === 'pip' && r.status !== 'acknowledged').length,
    completed: reviews.filter(r => new Date(r.review_date).getFullYear() === thisYear).length,
  };

  const columns = [
    { id: 'staff', header: 'Staff', size: 170, meta: { mobile: 'primary' }, cell: info => { const s = info.row.original.staff; return s ? (s.last_name ? `${s.last_name}, ${s.first_name}` : s.full_name) : '—'; } },
    { accessorKey: 'review_type', header: 'Type', size: 170, meta: { mobile: 'primary' }, cell: info => REVIEW_TYPE_LABEL[info.getValue()] || info.getValue() },
    { accessorKey: 'review_date', header: 'Date', size: 110, meta: { mobile: 'secondary' } },
    { accessorKey: 'overall_rating', header: 'Rating', size: 90, meta: { mobile: 'secondary' }, cell: info => info.getValue() != null ? `${info.getValue()}/5` : '—' },
    { id: 'status', header: 'Status', size: 130, enableSorting: false, meta: { mobile: 'primary' }, cell: info => <StatusPill status={statusColor(info.row.original.status)} /> },
    {
      id: 'actions', header: '', size: 90, enableSorting: false, meta: { mobile: 'secondary' },
      cell: info => <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} onClick={() => openEdit(info.row.original)}>Edit</button>,
    },
  ];

  const selectedStaff = staffOptions.find(s => s.id === selectedStaffId) || staffOptions[0];
  const selectedStaffReviews = selectedStaff ? (reviewsByStaff[selectedStaff.id] || []).sort((a, b) => new Date(b.review_date) - new Date(a.review_date)) : [];
  const selectedDueStatus = selectedStaff ? reviewDueStatus(selectedStaff, selectedStaffReviews) : null;

  return (
    <div>
      {error && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--danger)' }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 14 }}>
        <StatTile label="Reviews Overdue" value={stats.overdue} tone={stats.overdue > 0 ? 'alert' : 'neutral'} />
        <StatTile label="Due ≤30 Days" value={stats.dueSoon} tone={stats.dueSoon > 0 ? 'warn' : 'neutral'} />
        <StatTile label="Open PIPs" value={stats.openPips} tone={stats.openPips > 0 ? 'alert' : 'neutral'} />
        <StatTile label={`Completed in ${thisYear}`} value={stats.completed} tone="neutral" />
      </div>

      <div className="card">
        <ProfileTabBar
          tabs={[
            { key: 'all', label: 'All Reviews', badge: reviews.length },
            { key: 'staff', label: 'By Staff Member' },
          ]}
          activeKey={tab}
          onSelect={setTab}
        />

        {tab === 'all' && (
          <>
            <div style={{ padding: '10px 16px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', borderBottom: '0.5px solid var(--border)' }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button onClick={() => setTypeFilter('All')} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid', background: typeFilter === 'All' ? 'var(--accent)' : 'transparent', color: typeFilter === 'All' ? '#fff' : 'var(--text3)', borderColor: typeFilter === 'All' ? 'var(--accent)' : 'var(--border)' }}>All</button>
                {REVIEW_TYPES.map(t => (
                  <button key={t.key} onClick={() => setTypeFilter(t.key)} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid', background: typeFilter === t.key ? 'var(--accent)' : 'transparent', color: typeFilter === t.key ? '#fff' : 'var(--text3)', borderColor: typeFilter === t.key ? 'var(--accent)' : 'var(--border)' }}>{t.label}</button>
                ))}
              </div>
              <select value={staffFilter} onChange={e => setStaffFilter(e.target.value)} style={{ padding: '5px 8px', borderRadius: 8, fontSize: 12, border: '0.5px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)' }}>
                <option value="">All staff</option>
                {staffOptions.map(s => <option key={s.id} value={s.id}>{s.last_name ? `${s.last_name}, ${s.first_name}` : s.full_name}</option>)}
              </select>
              <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => openAdd()}>+ Add Review</button>
            </div>
            <DataTable tableId="performance-reviews" columns={columns} data={filtered} getRowId={r => r.id} loading={loading} emptyMessage="No performance reviews match this filter." maxHeight="calc(100vh - 380px)" />
          </>
        )}

        {tab === 'staff' && selectedStaff && (
          <div style={{ padding: 16 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
              {staffOptions.map(s => (
                <button key={s.id} onClick={() => setSelectedStaffId(s.id)} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid', background: (selectedStaff.id === s.id) ? 'var(--accent)' : 'transparent', color: (selectedStaff.id === s.id) ? '#fff' : 'var(--text3)', borderColor: (selectedStaff.id === s.id) ? 'var(--accent)' : 'var(--border)' }}>{s.last_name ? `${s.last_name}, ${s.first_name}` : s.full_name}</button>
              ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, padding: '12px 16px', borderRadius: 10, background: selectedDueStatus.bg, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: selectedDueStatus.color }}>
                Next review: {selectedDueStatus.label}{selectedDueStatus.dueDate ? ` — ${format(selectedDueStatus.dueDate, 'MMM d, yyyy')}` : ''}
              </span>
              <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => openAdd(selectedStaff.id)}>+ New Review</button>
            </div>

            {selectedStaffReviews.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>No reviews on file yet.</div>
            ) : selectedStaffReviews.map(r => (
              <div key={r.id} className="card" style={{ marginBottom: 12, border: '0.5px solid var(--border)' }}>
                <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderBottom: '0.5px solid var(--border)' }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{REVIEW_TYPE_LABEL[r.review_type]}</span>
                  <span style={{ fontSize: 12, color: 'var(--text3)' }}>{format(new Date(r.review_date), 'MMM d, yyyy')}</span>
                  {r.overall_rating != null && pill(`${r.overall_rating}/5`, { background: 'var(--accent-bg)', color: 'var(--accent)' })}
                  <StatusPill status={statusColor(r.status)} />
                  <button className="btn btn-outline btn-sm" style={{ marginLeft: 'auto', fontSize: 11 }} onClick={() => openEdit(r)}>Edit</button>
                </div>
                <div style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
                    {REVIEW_COMPETENCIES.map(c => r.ratings?.[c.key] ? (
                      <span key={c.key} style={{ fontSize: 11, color: 'var(--text3)' }}>{c.label}: <strong style={{ color: 'var(--text)' }}>{r.ratings[c.key]}/5</strong></span>
                    ) : null)}
                  </div>
                  {r.strengths && <div style={{ fontSize: 12, marginBottom: 6 }}><strong>Strengths:</strong> {r.strengths}</div>}
                  {r.areas_for_improvement && <div style={{ fontSize: 12, marginBottom: 6 }}><strong>Areas for Improvement:</strong> {r.areas_for_improvement}</div>}
                  {r.goals_next_period && <div style={{ fontSize: 12, marginBottom: 6 }}><strong>Goals:</strong> {r.goals_next_period}</div>}
                  {r.staff_acknowledged && <div style={{ fontSize: 11, color: 'var(--success)', marginTop: 6 }}>✓ Acknowledged by {r.signed_name || '—'} · {r.signed_at ? format(new Date(r.signed_at), 'MMM d, yyyy') : ''}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: 620 }}>
            <div className="modal-header">
              <div className="modal-title">{editing ? `Edit Review — ${editing.staff?.full_name || ''}` : 'New Performance Review'}</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
              {error && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--danger)' }}>{error}</div>}

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Staff Member *</label>
                  <select className="form-select" value={form.staff_id} onChange={e => setForm(p => ({ ...p, staff_id: e.target.value }))} disabled={!!editing}>
                    <option value="">Select staff…</option>
                    {staffOptions.map(s => <option key={s.id} value={s.id}>{s.last_name ? `${s.last_name}, ${s.first_name}` : s.full_name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Review Type</label>
                  <select className="form-select" value={form.review_type} onChange={e => setForm(p => ({ ...p, review_type: e.target.value }))}>
                    {REVIEW_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Review Date *</label>
                  <input type="date" className="form-input" value={form.review_date} onChange={e => setForm(p => ({ ...p, review_date: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Period Covered</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input type="date" className="form-input" value={form.period_start} onChange={e => setForm(p => ({ ...p, period_start: e.target.value }))} />
                    <input type="date" className="form-input" value={form.period_end} onChange={e => setForm(p => ({ ...p, period_end: e.target.value }))} />
                  </div>
                </div>
              </div>

              {form.review_type === 'pip' && (
                <div className="form-group">
                  <label className="form-label">Related Incident (optional)</label>
                  <select className="form-select" value={form.related_incident_id} onChange={e => setForm(p => ({ ...p, related_incident_id: e.target.value }))}>
                    <option value="">— None —</option>
                    {incidentOptions.map(i => <option key={i.id} value={i.id}>{i.incident_date} — {i.category?.replace('_', ' ')}</option>)}
                  </select>
                </div>
              )}

              <div className="form-label" style={{ marginTop: 8, marginBottom: 6 }}>Competency Ratings (1–5)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 8 }}>
                {REVIEW_COMPETENCIES.map(c => (
                  <div className="form-group" key={c.key} style={{ marginBottom: 0 }}>
                    <label className="form-label" style={{ fontSize: 11 }}>{c.label}</label>
                    <select className="form-select" value={form.ratings[c.key]} onChange={e => setForm(p => ({ ...p, ratings: { ...p.ratings, [c.key]: e.target.value } }))}>
                      <option value="">—</option>
                      {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                ))}
              </div>

              <div className="form-group">
                <label className="form-label">Strengths</label>
                <textarea className="form-textarea" rows={2} value={form.strengths} onChange={e => setForm(p => ({ ...p, strengths: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Areas for Improvement</label>
                <textarea className="form-textarea" rows={2} value={form.areas_for_improvement} onChange={e => setForm(p => ({ ...p, areas_for_improvement: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Goals for Next Period</label>
                <textarea className="form-textarea" rows={2} value={form.goals_next_period} onChange={e => setForm(p => ({ ...p, goals_next_period: e.target.value }))} />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                    {STATUS_OPTS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ background: 'var(--bg2)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '10px 14px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', marginBottom: form.staff_acknowledged ? 10 : 0 }}>
                  <input type="checkbox" checked={form.staff_acknowledged} onChange={e => setForm(p => ({ ...p, staff_acknowledged: e.target.checked }))} />
                  Staff acknowledged this review
                </label>
                {form.staff_acknowledged && (
                  <div className="form-row">
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Signed Name</label>
                      <input className="form-input" value={form.signed_name} onChange={e => setForm(p => ({ ...p, signed_name: e.target.value }))} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Signed Date</label>
                      <input type="date" className="form-input" value={form.signed_at} onChange={e => setForm(p => ({ ...p, signed_at: e.target.value }))} />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : editing ? 'Save Changes' : 'Save Review'}</button>
            </div>
          </div>
        </div>
      )}

      <Toast {...(toast || {})} onClose={hideToast} />
    </div>
  );
}
